/** SUNTRONIX ONE — Deployment Control Centre (§37, §38, §39).
 *  Default view is "Action Required" so ops don't wade through hundreds
 *  of completed deployments (§38). */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Clock, Package, Truck, DollarSign, ShieldCheck, FileText } from "lucide-react";
import VerifyDrawer from "@/components/app/VerifyDrawer";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Download the deployment certificate PDF for a given event via the
 *  authenticated axios client so the auth token & tenant scope are
 *  honoured, then push it to the browser as a blob download. */
async function downloadCertificate(eventId, label) {
  if (!eventId) {
    toast.error("Certificate unavailable — deployment not yet verified.");
    return;
  }
  try {
    const res = await api.get(`/deployments/certificate/${eventId}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `deployment-certificate-${(label || eventId).toString().slice(0, 24)}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Certificate download failed");
  }
}

export default function DeploymentControlCentre() {
  const [queue, setQueue] = useState(null);
  const [work, setWork] = useState(null);
  const [tab, setTab] = useState("action_required");
  const [loading, setLoading] = useState(true);
  const [verifyRequestId, setVerifyRequestId] = useState(null);

  const reload = () => {
    Promise.all([
      api.get("/deployments/queue"),
      api.get("/deployments/my-work"),
    ]).then(([q, w]) => { setQueue(q.data); setWork(w.data); })
      .catch(() => toast.error("Failed to load deployment queue"));
  };

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([
      api.get("/deployments/queue"),
      api.get("/deployments/my-work"),
    ]).then(([q, w]) => {
      if (cancel) return;
      setQueue(q.data);
      setWork(w.data);
    }).catch(() => toast.error("Failed to load deployment queue"))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);
  if (loading || !queue) {
    return <div className="p-6 text-sm text-[color:var(--st-text-muted)]">Loading deployment centre…</div>;
  }

  const buckets = queue.buckets || {};
  const kpis = [
    { key: "action_required", label: "Action required", value: buckets.action_required, icon: AlertTriangle, tone: "warn" },
    { key: "exceptions", label: "Exceptions", value: buckets.exceptions, icon: AlertTriangle, tone: "err" },
    { key: "in_transit", label: "In transit", value: buckets.in_transit, icon: Truck, tone: "neutral" },
    { key: "preparing", label: "Preparing", value: buckets.preparing, icon: Package, tone: "neutral" },
    { key: "deployed", label: "Deployed (today)", value: buckets.deployed, icon: CheckCircle2, tone: "ok" },
    { key: "planned", label: "Planned", value: buckets.planned, icon: Clock, tone: "neutral" },
  ];

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="deployment-centre">
      <PageHeader
        eyebrow="Operations · Deploy"
        title="Deployment Control Centre"
        context="One shows only work that remains — completed deployments stay out of your queue."
        testId="deployment-centre-header"
      />

      <Section title="Today" testId="deployment-buckets">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {kpis.map((k) => {
            const Icon = k.icon;
            const tint = k.tone === "warn" ? "text-amber-700" : k.tone === "err" ? "text-red-700" : k.tone === "ok" ? "text-emerald-700" : "text-[color:var(--st-text)]";
            return (
              <button
                key={k.key}
                onClick={() => setTab(k.key)}
                className={"st-panel p-3 text-left transition-colors " + (tab === k.key ? "ring-2 ring-[color:var(--st-accent)]" : "hover:bg-[color:var(--st-surface-alt)]")}
                data-testid={`deployment-kpi-${k.key}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">{k.label}</div>
                  <Icon className={`h-3.5 w-3.5 ${tint}`} strokeWidth={1.6} />
                </div>
                <div className={`text-2xl font-heading font-semibold mt-1 ${tint}`}>{k.value ?? 0}</div>
              </button>
            );
          })}
        </div>
      </Section>

      {tab === "action_required" && <ActionRequiredPanel tasks={queue.action_tasks || []} byCategory={work?.by_category || {}} onVerify={setVerifyRequestId} onCertificate={downloadCertificate} />}
      {tab === "deployed" && <RecentDeployedPanel />}
      {tab !== "action_required" && tab !== "deployed" && (
        <Section title={kpis.find((k) => k.key === tab)?.label || tab}>
          <div className="text-[13px] text-[color:var(--st-text-muted)] p-4 text-center">
            List for this bucket coming soon. Use &quot;Action required&quot; as your primary lens.
          </div>
        </Section>
      )}

      {work?.billing_candidates?.length > 0 && (
        <Section title="Ready to bill" hint="Deployment completion events with an active commercial contract." testId="deployment-billing">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {work.billing_candidates.map((b) => (
              <div key={b.id} className="st-panel p-3 flex items-start gap-3" data-testid={`deployment-billing-${b.id}`}>
                <DollarSign className="h-4 w-4 text-emerald-700 mt-0.5" strokeWidth={1.6} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{b.service} — {b.quantity} unit(s)</div>
                  <div className="text-[11px] text-[color:var(--st-text-subtle)]">{b.customer} · {b.currency} · {b.billing_entity || "—"}</div>
                  <div className="mt-1">
                    <span className={"px-1.5 py-0.5 rounded text-[10px] " + (b.status === "ready_to_bill" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}>
                      {b.status === "ready_to_bill" ? "Ready to bill" : "PO missing"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {verifyRequestId && (
        <VerifyDrawer requestId={verifyRequestId}
                      onClose={() => setVerifyRequestId(null)}
                      onVerified={() => { setVerifyRequestId(null); reload(); }} />
      )}
    </div>
  );
}

function ActionRequiredPanel({ tasks, byCategory, onVerify, onCertificate }) {
  if (!tasks.length) {
    return (
      <Section title="Action required" testId="deployment-action-panel">
        <div className="st-panel p-6 text-center text-[13px] text-emerald-700">
          <CheckCircle2 className="h-6 w-6 mx-auto mb-2" strokeWidth={1.6} />
          Nothing needs attention. All deployments are on track.
        </div>
      </Section>
    );
  }
  return (
    <Section
      title={`Action required · ${tasks.length}`}
      hint="Only the outstanding human actions from verified deployments."
      testId="deployment-action-panel"
    >
      {Object.keys(byCategory).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {Object.entries(byCategory).map(([cat, n]) => (
            <span key={cat} className="px-2 py-0.5 rounded-md bg-[color:var(--st-surface-alt)] text-[11px]" data-testid={`action-cat-${cat}`}>
              {cat}: <b>{n}</b>
            </span>
          ))}
        </div>
      )}
      <div className="st-panel divide-y divide-[color:var(--st-border)]">
        {tasks.map((t) => (
          <div key={t.id} className="p-3 flex items-start gap-3" data-testid={`deployment-task-${t.id}`}>
            <AlertTriangle className={`h-4 w-4 mt-0.5 ${t.priority === "high" ? "text-red-600" : "text-amber-600"}`} strokeWidth={1.6} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{t.check_name}</div>
              <div className="text-[12px] text-[color:var(--st-text-muted)]">{t.reason}</div>
              <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-[color:var(--st-text-subtle)]">
                <span>{t.customer}</span>
                {t.employee_email && <span>· {t.employee_email}</span>}
                <span>· owner: <b>{t.owner_role}</b></span>
                <span>· category: {t.category}</span>
              </div>
            </div>
            <Link to={`/deployments/${t.request_id}/pre-acceptance`} className="st-btn st-btn-sm st-btn-ghost" data-testid={`deployment-preacceptance-${t.id}`}>
              Delivery
            </Link>
            <Link to={`/deployments/timeline/${t.request_id}`} className="st-btn st-btn-sm st-btn-ghost">
              Open
            </Link>
            {t.deployment_event_id && (
              <button
                onClick={() => onCertificate?.(t.deployment_event_id, t.employee_email || t.request_id)}
                data-testid={`deployment-certificate-${t.id}`}
                className="st-btn st-btn-sm st-btn-ghost inline-flex items-center gap-1"
                title="Download deployment certificate PDF"
              >
                <FileText className="h-3 w-3" strokeWidth={2} /> Certificate
              </button>
            )}
            <button onClick={() => onVerify?.(t.request_id)} data-testid={`deployment-verify-${t.id}`}
              className="st-btn st-btn-sm inline-flex items-center gap-1"
              style={{ background: "var(--st-accent)", color: "white" }}>
              <ShieldCheck className="h-3 w-3" strokeWidth={2} /> Verify
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function RecentDeployedPanel() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get("/deployments/recent?limit=25")
      .then((r) => { if (!cancel) setItems(r.data.items || []); })
      .catch(() => !cancel && setItems([]));
    return () => { cancel = true; };
  }, []);
  if (items === null) {
    return (
      <Section title="Deployed" hint="Recently verified deployments." testId="deployment-recent">
        <div className="text-[13px] text-[color:var(--st-text-muted)] p-4 text-center">Loading…</div>
      </Section>
    );
  }
  if (!items.length) {
    return (
      <Section title="Deployed" hint="Recently verified deployments." testId="deployment-recent">
        <div className="text-[13px] text-[color:var(--st-text-muted)] p-4 text-center">
          No deployments verified yet.
        </div>
      </Section>
    );
  }
  const verdictClass = (v) => (
    v === "DEPLOYED_COMPLETE" ? "bg-emerald-50 text-emerald-700" :
    v === "DEPLOYMENT_EXCEPTION" ? "bg-red-50 text-red-700" :
    "bg-amber-50 text-amber-700"
  );
  return (
    <Section title={`Deployed · ${items.length}`} hint="Recently verified deployments — download the certificate to share with the customer." testId="deployment-recent">
      <div className="st-panel divide-y divide-[color:var(--st-border)]">
        {items.map((e) => (
          <div key={e.id} className="p-3 flex items-start gap-3" data-testid={`deployment-recent-${e.id}`}>
            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" strokeWidth={1.6} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{e.customer || "—"} · {e.employee_email || "—"}</div>
              <div className="text-[11px] text-[color:var(--st-text-subtle)] mt-0.5">
                {e.serial || e.asset_id || "no serial"} · verified {e.verified_at ? new Date(e.verified_at).toLocaleString() : "—"}
              </div>
              <div className="mt-1">
                <span className={"px-1.5 py-0.5 rounded text-[10px] " + verdictClass(e.verdict)}>
                  {(e.verdict || "").replace(/_/g, " ")}
                </span>
              </div>
            </div>
            <button
              onClick={() => downloadCertificate(e.id, e.employee_email || e.serial || e.id)}
              data-testid={`deployment-recent-certificate-${e.id}`}
              className="st-btn st-btn-sm inline-flex items-center gap-1"
              style={{ background: "var(--st-accent)", color: "white" }}
              title="Download deployment certificate PDF"
            >
              <FileText className="h-3 w-3" strokeWidth={2} /> Certificate
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}
